import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { BW_TIPS } from "../data/bwTips";
import { engModeAtom } from "../store/atoms";

const ROTATE_INTERVAL_MS = 7 * 1000;

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8787";

interface TipSets { ko: string[]; en: string[] }
const FALLBACK_TIPS: TipSets = { ko: BW_TIPS, en: [] };

async function fetchTips(): Promise<TipSets> {
  const res = await fetch(`${API_URL}/api/tips`);
  if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
  const data = await res.json();
  return {
    ko: Array.isArray(data.tips) && data.tips.length > 0 ? data.tips : BW_TIPS,
    en: Array.isArray(data.tipsEn) ? data.tipsEn : [],
  };
}

// 로딩 화면·검색 0건 화면용 잡학 팁 — 어드민 탭에서 관리하는 DB 목록을 받아온다. 네트워크가
// 느리거나 실패해도 항상 뭔가는 보여줘야 해서 번들에 박힌 BW_TIPS를 placeholderData/폴백으로 둔다.
// initialData를 쓰면 그 데이터를 "방금 fetch된 것"으로 취급해 staleTime이 지나기 전까진 실제
// 요청을 안 보내버림(2026-09-08, 프로덕션에서 폴백 2개만 계속 뜨는 버그로 발견) — placeholderData는
// 화면엔 즉시 보여주면서도 fetch는 정상적으로 나간다.
// Suspense를 쓰지 않는 일반 useQuery라 App.tsx 최상위 SuspenseLoader 안에서도 안전하게 호출 가능.
// ENG 모드는 영문 번역이 있는 팁만(2026-10-18) — 번역 없는 팁은 빌드 은어 표기가 확실하지 않아
// 일부러 비워둔 것이라 한국어로 섞어 보여주지 않는다. 빈 배열이면 화면에서 TIP 줄 자체를 숨김.
export function useTips(): string[] {
  const isEngMode = useRecoilValue(engModeAtom);
  const { data } = useQuery({
    queryKey: ["bwTips", "v2"],
    queryFn: fetchTips,
    placeholderData: FALLBACK_TIPS,
    staleTime: 60 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
  });
  const sets = data ?? FALLBACK_TIPS;
  return isEngMode ? sets.en : sets.ko;
}

export const isHangul = (s: string) => /[가-힣]/.test(s);

export function pickTip(tips: string[]): string {
  return tips.length > 0 ? tips[Math.floor(Math.random() * tips.length)] : "";
}

let persistentTipCache: { path: string; tip: string } | null = null;

// 한 페이지 방문 안에서 로딩이 여러 단계로 나뉠 때(App.tsx의 전역 SuspenseLoader → 페이지 자체
// 로더) 문장이 사라지는 문제는 고쳤는데, 단계마다 새로 랜덤을 뽑아서 다른 문장으로 바뀌는 게
// 여전히 어색했음(2026-09-08) — 경로가 같으면 이전에 뽑은 문장을 그대로 재사용해서 한 번의
// 로딩 여정 동안은 문장이 안 바뀌게 한다. 경로가 바뀌면(다른 페이지 방문) 자동으로 새로 뽑힘.
export function pickPersistentTip(tips: string[]): string {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  // ENG 전환으로 언어가 바뀌었으면 캐시된 문장을 버리고 새로 뽑음
  if (persistentTipCache && persistentTipCache.path === path && persistentTipCache.tip && isHangul(persistentTipCache.tip) === isHangul(tips[0] ?? "")) {
    return persistentTipCache.tip;
  }
  const tip = pickTip(tips);
  persistentTipCache = { path, tip };
  return tip;
}

// 로딩 스피너용 — 처음엔 pickPersistentTip으로 이어붙이고, 7초 넘게 로딩이 계속되면(네트워크가
// 느린 경우) 지루하지 않게 다른 팁으로 바꿔준다(2026-09-08 사용자 요청). 로테이션된 값도
// persistentTipCache에 반영해서 이어지는 다음 로딩 단계가 최신 문장을 물려받게 한다.
export function useLoadingTip(tips: string[]): string {
  const [tip, setTip] = useState(() => pickPersistentTip(tips));

  useEffect(() => {
    // ENG 전환으로 언어가 바뀌었을 때만 즉시 교체 — fetch 완료(폴백→DB 목록)로는 안 바꿈,
    // 같은 화면에서 문장이 눈에 띄게 바뀌어 보여서(2026-09-08)
    // 빈 문장(ENG 모드에서 영문 목록 도착 전)이면 도착하는 대로 채움
    if (!tip || isHangul(tip) !== isHangul(tips[0] ?? "")) setTip(pickPersistentTip(tips));
    const timer = setInterval(() => {
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      const next = pickTip(tips);
      persistentTipCache = { path, tip: next };
      setTip(next);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tips)]);

  return tip;
}
